import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GraduationCap } from 'lucide-react';

import { ConsoleIdentity } from '@/components/layout/ConsoleIdentity';
import { ConsoleLayout } from '@/components/layout/ConsoleLayout';
import { teacherNav } from '@/constants/navigation';
import { ROLE_LABEL } from '@/constants/roles';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { NotificationBell } from '@/features/notifications/components/NotificationBell';
import { useHeaderClass } from '@/features/teacher/store/headerClass.store';
import { useInstituteBranding } from '@/features/institute/store/branding.store';

/**
 * The teacher console.
 *
 * The same frame as the institute and platform consoles — it used to be a
 * fourth copy of the shell, written inside the dashboard page itself, and had
 * already drifted to a different sidebar width and, later, to the student
 * panel's colours. Staff share one look; the student area keeps its own.
 */
export function TeacherLayout() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const branding = useInstituteBranding((state) => state.branding);
  const loadBranding = useInstituteBranding((state) => state.load);

  // The institute the teacher belongs to names the sidebar as well as drawing
  // its heading — a college's teachers are shown "Assignments" where a school's
  // are shown "Homework".
  useEffect(() => {
    void loadBranding();
  }, [loadBranding]);

  const signedInAs = user?.instituteRole ?? (user ? ROLE_LABEL[user.role] : '');

  // The class the screen below is working in, when it has one; otherwise every
  // subject they take, as before.
  const classLabel = useHeaderClass((state) => state.label);
  const subjectLabel = classLabel ?? (user?.subjects?.length ? user.subjects.join(', ') : '');
  const roleWithSubjects = subjectLabel ? `${signedInAs} · ${subjectLabel}` : signedInAs;

  return (
    <ConsoleLayout
      nav={teacherNav(branding?.institutionType)}
      brand={{
        mark: <GraduationCap className="h-[17px] w-[17px]" strokeWidth={1.9} />,
        // The institute they teach at, never a name baked into the build (§4).
        title: branding?.displayName ?? 'Teacher portal',
        subtitle: 'Teacher portal',
      }}
      // No `account`: the top bar already names the teacher (`ConsoleIdentity`).
      onSignOut={async () => {
        await logout();
        navigate('/login', { replace: true });
      }}
      greeting={
        <ConsoleIdentity
          name={user?.fullName ?? 'Teacher'}
          role={roleWithSubjects}
        />
      }
      actions={<NotificationBell />}
    />
  );
}
